import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Badge } from 'primereact/badge';

import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails } from '../../service/ProductService';
import { GET_DASHBOARD } from '../../config/ApiConstant';
import AuthContext from '../../context/authContext';
import { defaultRowOptions, defaultPageRowOptions, paginatorLinks, showingEntries } from '../../appconfig/Settings';
import { formatDateTime } from '../../appconfig/AppHelper';

const Dashboard = () => {
    document.title = 'Dashboard | Karan Enterprise';

    const { userDetails }: any = useContext(AuthContext);
    const navigate = useNavigate();

    const role_id = !window.cn(userDetails) && userDetails?.role_id;

    const [productCounts, setProductCounts] = useState<any>([]);
    const [todayDispatches, setTodayDispatches] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any>([]);

    useEffect(() => {
        getDashboardData();
    }, []);

    const getDashboardData = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetails(GET_DASHBOARD);
            if (response) {
                setProductCounts(response?.data?.product_counts || []);
                setTodayDispatches(response?.data?.today_dispatches || []);
                setPageLoad(true);
            }
        } catch (error) {
            setPageLoad(false);
        }
    };

    const handleBoxClick = (item: any) => {
        navigate('/inventory-list', { state: { productData: item } });
    };

    const partyNameTemplate = (rowData: any) => {
        return <span className="font-bold">{rowData?.party?.party_name || '-'}</span>;
    };

    const dispatchDateTemplate = (rowData: any) => {
        return <span>{formatDateTime(rowData?.dispatch_date)}</span>;
    };

    const itemsCountTemplate = (rowData: any) => {
        return <span className="text-primary">{rowData?.items?.length || 0}</span>;
    };

    const statusTemplate = (rowData: any) => {
        const severity = rowData?.status === 'Approved' ? 'success' : rowData?.status === 'Rejected' ? 'danger' : 'warning';
        return <Badge severity={severity} value={rowData?.status?.toUpperCase()} />;
    };

    const rowExpansionTemplate = (data: any) => {
        return (
            <div className="p-3 surface-50 border-round">
                <h5 className="mb-3">Items for Dispatch #{data?.id}</h5>
                {data?.items && data?.items.length > 0 ? (
                    <DataTable value={data.items} stripedRows>
                        <Column field="roll.roll_id" header="Roll ID" sortable body={(rowData: any) => rowData?.roll?.roll_id || '-'} />
                        <Column field="roll.product.name" header="Product Name" sortable body={(rowData: any) => rowData?.roll?.product?.name || '-'} />
                        <Column field="roll.width" header="Width" sortable body={(rowData: any) => rowData?.roll?.width || '-'} />
                        <Column field="roll.length" header="Length" sortable body={(rowData: any) => rowData?.roll?.length || '-'} />
                        <Column field="roll.micron_gsm" header="Micron/GSM" sortable body={(rowData: any) => rowData?.roll?.micron_gsm || '-'} />
                        <Column
                            field="roll.is_owr"
                            header="OWR"
                            body={(rowData: any) => {
                                const isOwr = rowData?.roll?.is_owr === 1;
                                return <Badge severity={isOwr ? 'success' : 'danger'} value={isOwr ? 'Yes' : 'No'} />;
                            }}
                        />
                        <Column
                            field="roll.without_glase"
                            header="Without Glase"
                            body={(rowData: any) => {
                                const isWithoutGlase = rowData?.roll?.without_glase === 1;
                                return <Badge severity={isWithoutGlase ? 'success' : 'danger'} value={isWithoutGlase ? 'Yes' : 'No'} />;
                            }}
                        />
                    </DataTable>
                ) : (
                    <p className="text-gray-500">No items found</p>
                )}
            </div>
        );
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Dashboard'} pageName={'Dashboard'} pageTitle={'Dashboard'} />

            <div className="layout-dashboard mb-4">
                <div className="grid">
                    {productCounts.map((item: any, index: number) => (
                        <div key={index} className="col-12 md:col-3">
                            <div className="card widget-overview-box widget-overview-box-1 total-count-card cursor-pointer" onClick={() => handleBoxClick(item)}>
                                <span className="overview-title">{item?.product_name || 'Product'}</span>
                                <div className="flex justify-content-between align-items-center">
                                    <div className="overview-text">{item?.count || 0}</div>
                                    <img src="/assets/images/employee.gif" className="img-fluid" alt="logo" width={40} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid">
                <div className="col-12">
                    <div className="card">
                        <h3 className="mb-3">Today's Dispatches</h3>
                        <DataTable
                            className="datatable-responsive"
                            value={todayDispatches}
                            paginator={todayDispatches.length >= 10 && true}
                            rows={defaultRowOptions}
                            rowsPerPageOptions={defaultPageRowOptions}
                            paginatorTemplate={paginatorLinks}
                            currentPageReportTemplate={showingEntries}
                            emptyMessage="No Dispatch Data Found"
                            showGridlines
                            stripedRows
                            expandedRows={expandedRows}
                            onRowToggle={(e) => setExpandedRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                            dataKey="id"
                        >
                            <Column expander style={{ width: '3rem' }} />
                            <Column field="party_name" header="Party Name" sortable body={partyNameTemplate} />
                            <Column field="dispatch_date" header="Dispatch Date" sortable body={dispatchDateTemplate} />
                            <Column field="items" header="Items Count" sortable body={itemsCountTemplate} />
                            <Column field="status" header="Status" sortable body={statusTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
